import React, { useState, useEffect } from 'react';
import axios from 'axios';
import useNewsRefresh from '../../../hooks/useNewsRefresh';
import './NewsAnalysis.css';

const NewsAnalysis = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedNews, setSelectedNews] = useState(null);
  const [impactAnalysis, setImpactAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [filters, setFilters] = useState({
    category: 'all',
    sentiment: 'all',
    timeframe: '24h'
  });

  // Get backend URL for API calls
  const getBackendUrl = () => {
    // First, try React environment variables
    if (process.env.REACT_APP_BACKEND_URL) {
      return process.env.REACT_APP_BACKEND_URL;
    }
    
    // Second, try regular environment variables
    if (process.env.BACKEND_URL) {
      return process.env.BACKEND_URL;
    }
    
    // Third, check if we're running on Firebase Hosting and use Cloud Run backend
    if (typeof window !== 'undefined' && window.location.hostname) {
      const hostname = window.location.hostname;
      
      if (hostname.includes('firebaseapp.com') || 
          hostname.includes('web.app') ||
          hostname.includes('viraltrade.in') ||
          hostname.includes('viraltrade-19032')) {
        return 'https://viral-trade-backend-1081469852720.asia-south1.run.app';
      }
      
      // Fourth, check if we're running on Cloud Run and use hardcoded URL
      if (hostname.includes('run.app')) {
        return 'https://viral-trade-backend-1081469852720.asia-south1.run.app';
      }
    }
    
    // Finally, fallback to localhost for development
    return 'http://localhost:8000';
  };

  const BASE_URL = getBackendUrl();

  const fetchNews = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Build query parameters
      const params = new URLSearchParams({
        limit: '20',
        offset: '0'
      });

      if (filters.category && filters.category !== 'all') {
        params.append('category', filters.category);
      }

      if (filters.sentiment && filters.sentiment !== 'all') {
        params.append('sentiment', filters.sentiment);
      }

      if (filters.timeframe) {
        params.append('timeframe', filters.timeframe);
      }

      const response = await axios.get(`${BASE_URL}/api/news-display/news?${params.toString()}`);
      
      if (response.data.success) {
        // Transform backend data to frontend format
        const transformedNews = response.data.data.map(item => ({
          id: item.id,
          title: item.title,
          summary: item.summary,
          source: item.source,
          publishedAt: item.published_at,
          category: item.category,
          sentiment: item.sentiment?.sentiment || 'neutral', // Handle nested sentiment structure
          url: item.url,
          severity: item.severity,
          affectedSectors: item.affected_sectors || [],
          affectedCompanies: item.affected_companies || [],
          // ✅ Store complete analysis data for immediate use
          sentimentAnalysis: item.sentiment || null,
          impactAnalysis: item.impact_analysis || null,
          isAnalyzed: item.is_analyzed || false
        }));
        
        setNews(transformedNews);
      } else {
        setError('Failed to fetch news. Please try again.');
      }
    } catch (err) {
      console.error('Failed to fetch news:', err);
      setError('Failed to fetch news. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Use the news refresh hook for automatic refresh on page load
  const { refreshNews } = useNewsRefresh(fetchNews, [filters]);

  useEffect(() => {
    // Initial fetch on component mount
    fetchNews();
    
    // Check if we should refresh on page load (if more than 1 minute has passed since last refresh)
    const storedTime = localStorage.getItem('newsLastRefreshTime');
    if (storedTime) {
      const lastRefresh = new Date(storedTime).getTime();
      const now = new Date().getTime();
      const timeDiff = now - lastRefresh;
      const oneMinute = 60 * 1000;
      
      if (timeDiff >= oneMinute) {
        console.log('Page refreshed - fetching latest news');
        refreshNews();
      } else {
        console.log('Page refreshed - using cached news (refresh cooldown active)');
      }
    } else {
      // First time loading - fetch news
      console.log('First time loading - fetching news');
    }
  }, []); // Only run on mount

  useEffect(() => {
    // Fetch news when filters change (this doesn't count as a refresh)
    fetchNews();
  }, [filters]);

  const analyzeNewsImpact = async (newsItem) => {
    setSelectedNews(newsItem);
    setAnalyzing(false); // Don't show analyzing state if we have stored data
    setImpactAnalysis(null);

    // ✅ FIRST: Check if we already have stored analysis data
    if (newsItem.impactAnalysis && newsItem.isAnalyzed) {
      console.log('Using stored analysis data for:', newsItem.title);
      
      // Transform stored analysis data to frontend format
      const storedAnalysis = newsItem.impactAnalysis;
      const transformedAnalysis = {
        overallImpact: storedAnalysis.overall_impact || 'unknown',
        sectorImpacts: (storedAnalysis.sector_impacts || []).map(sector => ({
          sector: sector.sector,
          impact: sector.impact,
          confidence: sector.confidence,
          reasoning: sector.reasoning,
          affectedStocks: sector.affected_stocks || []
        })),
        marketSentiment: {
          current: storedAnalysis.market_sentiment?.current || 'stable',
          predicted: storedAnalysis.market_sentiment?.predicted || 'minimal_change',
          confidence: storedAnalysis.market_sentiment?.confidence || 0.5
        },
        riskAssessment: {
          level: storedAnalysis.risk_assessment?.level || 'low',
          factors: storedAnalysis.risk_assessment?.factors || [],
          mitigation: storedAnalysis.market_sentiment?.mitigation || 'Monitor updates'
        },
        recommendations: storedAnalysis.recommendations || []
      };
      
      setImpactAnalysis(transformedAnalysis);
      return; // ✅ Exit early - no need to call backend
    }

    // ✅ SECOND: Only call processing route if analysis is missing
    console.log('No stored analysis found, calling processing route for:', newsItem.title);
    setAnalyzing(true);

    try {
      // Call the backend API for impact analysis
      const response = await axios.post(`${BASE_URL}/api/news-processing/analyze-news`, {
        title: newsItem.title,
        content: newsItem.summary
      });
      
      if (response.data.success) {
        const analysisData = response.data.data;
        
        // Transform backend data to frontend format
        const transformedAnalysis = {
          overallImpact: analysisData.impact_analysis?.overall_impact || 'unknown',
          sectorImpacts: (analysisData.impact_analysis?.sector_impacts || []).map(sector => ({
            sector: sector.sector,
            impact: sector.impact,
            confidence: sector.confidence,
            reasoning: sector.reasoning,
            affectedStocks: sector.affected_stocks || []
          })),
          marketSentiment: {
            current: analysisData.impact_analysis?.market_sentiment?.current || 'stable',
            predicted: analysisData.impact_analysis?.market_sentiment?.predicted || 'minimal_change',
            confidence: analysisData.impact_analysis?.market_sentiment?.confidence || 0.5
          },
          riskAssessment: {
            level: analysisData.impact_analysis?.risk_assessment?.level || 'low',
            factors: analysisData.impact_analysis?.risk_assessment?.factors || [],
            mitigation: analysisData.impact_analysis?.risk_assessment?.mitigation || 'Monitor updates'
          },
          recommendations: analysisData.impact_analysis?.recommendations || []
        };
        
        setImpactAnalysis(transformedAnalysis);
      } else {
        setError('Failed to analyze news impact. Please try again.');
      }
    } catch (err) {
      console.error('Failed to analyze news impact:', err);
      setError('Failed to analyze news impact. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Date unavailable';
      }
      
      const now = new Date();
      const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
      
      if (diffInHours < 1) return 'Just now';
      if (diffInHours < 24) return `${diffInHours}h ago`;
      if (diffInHours < 48) return 'Yesterday';
      return date.toLocaleDateString();
    } catch (error) {
      return 'Date unavailable';
    }
  };

  const getSentimentIcon = (sentiment) => {
    switch (sentiment) {
      case 'positive': return '📈';
      case 'negative': return '📉';
      case 'neutral': return '📊';
      default: return '❓';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'high-severity';
      case 'medium': return 'medium-severity';
      case 'low': return 'low-severity';
      default: return '';
    }
  };

  const getImpactColor = (impact) => {
    switch (impact) {
      case 'positive': return 'positive-impact';
      case 'negative': return 'negative-impact';
      case 'neutral': return 'neutral-impact';
      default: return '';
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const filteredNews = news.filter(item => {
    if (filters.category !== 'all' && item.category !== filters.category) return false;
    if (filters.sentiment !== 'all' && item.sentiment !== filters.sentiment) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="news-analysis">
        <div className="loading-placeholder">
          <div className="loading-spinner">⏳</div>
          <p>Loading news analysis...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="news-analysis">
      <div className="analysis-header">
        <h2>📰 News Analysis & Impact Assessment</h2>
        <p>Analyze how recent news events impact companies, sectors, and market sentiment</p>
      </div>

      <div className="filters-section">
        <div className="filter-group">
          <label htmlFor="category">Category:</label>
          <select
            id="category"
            value={filters.category}
            onChange={(e) => handleFilterChange('category', e.target.value)}
          >
            <option value="all">All Categories</option>
            <option value="aviation">Aviation</option>
            <option value="trade">Trade</option>
            <option value="technology">Technology</option>
            <option value="finance">Finance</option>
            <option value="energy">Energy</option>
            <option value="healthcare">Healthcare</option>
            <option value="automotive">Automotive</option>
            <option value="cryptocurrency">Cryptocurrency</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="sentiment">Sentiment:</label>
          <select
            id="sentiment"
            value={filters.sentiment}
            onChange={(e) => handleFilterChange('sentiment', e.target.value)}
          >
            <option value="all">All Sentiments</option>
            <option value="positive">Positive</option>
            <option value="negative">Negative</option>
            <option value="neutral">Neutral</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="timeframe">Timeframe:</label>
          <select
            id="timeframe"
            value={filters.timeframe}
            onChange={(e) => handleFilterChange('timeframe', e.target.value)}
          >
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">❌</span>
          {error}
        </div>
      )}

      <div className="news-grid">
        {filteredNews.map((item) => (
          <div key={item.id} className="news-card">
            <div className="news-header">
              <span className={`severity-badge ${getSeverityColor(item.severity)}`}>
                {(item.severity || 'unknown').toUpperCase()}
              </span>
              <span className="sentiment-icon">{getSentimentIcon(item.sentiment || 'neutral')}</span>
              <span className="news-source">{item.source || 'Unknown Source'}</span>
              <span className="news-time">{formatDate(item.publishedAt || new Date())}</span>
            </div>

            <div className="news-content">
              <h4 className="news-title">
                <a href={item.url || '#'} target="_blank" rel="noopener noreferrer">
                  {item.title || 'No title available'}
                </a>
              </h4>
              <p className="news-summary">{item.summary || 'No summary available'}</p>
              
              <div className="affected-entities">
                <div className="affected-sectors">
                  <strong>Affected Sectors:</strong>
                  <div className="sector-tags">
                    {(item.affectedSectors || []).map((sector, index) => (
                      <span key={index} className="sector-tag">{sector}</span>
                    ))}
                  </div>
                </div>
                
                <div className="affected-companies">
                  <strong>Affected Companies:</strong>
                  <div className="company-tags">
                    {(item.affectedCompanies || []).map((company, index) => (
                      <span key={index} className="company-tag">{company}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="news-actions">
              <button
                className={`analyze-btn ${item.isAnalyzed ? 'analyzed' : ''}`}
                onClick={() => analyzeNewsImpact(item)}
                disabled={analyzing}
              >
                {analyzing ? '🔍 Analyzing...' : 
                 item.isAnalyzed ? '📊 View Analysis' : '🔍 Analyze Impact'}
              </button>

            </div>
          </div>
        ))}
      </div>

      {!loading && filteredNews.length === 0 && (
        <div className="no-results">
          <div className="no-results-icon">🔍</div>
          <h3>No News Found</h3>
          <p>No news articles match your current filters. Try adjusting the criteria.</p>
        </div>
      )}

      {/* Impact Analysis Modal */}
      {selectedNews && (
        <div className="impact-modal-overlay" onClick={() => setSelectedNews(null)}>
          <div className="impact-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Impact Analysis: {selectedNews?.title || 'Unknown News'}</h3>
              <button className="close-btn" onClick={() => setSelectedNews(null)}>×</button>
            </div>
            
            {analyzing && (
              <div className="analyzing-placeholder">
                <div className="analyzing-spinner">🤖</div>
                <p>AI is analyzing the impact of this news event...</p>
                <p className="analyzing-details">Analyzing sector impacts, market sentiment, and risk factors</p>
              </div>
            )}

            {impactAnalysis && (
              <div className="impact-content">
                                 <div className="impact-summary">
                   <div className="impact-header-row">
                     <h4>Overall Impact: <span className={`impact-level ${getImpactColor(impactAnalysis.overallImpact)}`}>
                       {(impactAnalysis.overallImpact || 'unknown').toUpperCase()}
                     </span></h4>
                     
                     {/* URL button in same row */}
                     {selectedNews?.url && selectedNews.url.trim() !== '' ? (
                       <a 
                         href={selectedNews.url} 
                         target="_blank" 
                         rel="noopener noreferrer"
                         className="impact-read-article-btn"
                       >
                         📰 Read Full Article
                       </a>
                     ) : (
                       <span className="impact-no-url-message">
                         📰 Source article not available
                       </span>
                     )}
                   </div>
                   
                   {/* Affected Sectors and Companies Section */}
                   <div className="affected-entities-summary">
                     <div className="affected-sectors-summary">
                       <h5>🏢 Affected Sectors</h5>
                       <div className="sector-tags-summary">
                         {(selectedNews?.affectedSectors || []).length > 0 ? (
                           selectedNews.affectedSectors.map((sector, index) => (
                             <span key={index} className="sector-tag-summary">{sector}</span>
                           ))
                         ) : (
                           <span className="no-data-message">No sectors identified</span>
                         )}
                       </div>
                     </div>
                     
                     <div className="affected-companies-summary">
                       <h5>🏭 Affected Companies</h5>
                       <div className="company-tags-summary">
                         {(selectedNews?.affectedCompanies || []).length > 0 ? (
                           selectedNews.affectedCompanies.map((company, index) => (
                             <span key={index} className="company-tag-summary">{company}</span>
                           ))
                         ) : (
                           <span className="no-data-message">No companies identified</span>
                         )}
                       </div>
                     </div>
                   </div>
                 </div>

                <div className="impact-details">
                  <div className="impact-section">
                    <h5>📊 Sector Impacts</h5>
                    {(impactAnalysis.sectorImpacts || []).map((sector, index) => (
                      <div key={index} className="sector-impact">
                        <div className="sector-header">
                          <span className="sector-name">{sector.sector || 'Unknown Sector'}</span>
                          <span className={`impact-badge ${getImpactColor(sector.impact)}`}>
                            {sector.impact || 'unknown'}
                          </span>
                          <span className="confidence-score">{Math.round((sector.confidence || 0) * 100)}%</span>
                        </div>
                        <p className="impact-reasoning">{sector.reasoning || 'No reasoning available.'}</p>
                        <div className="affected-stocks">
                          <strong>Affected Stocks:</strong> {(sector.affectedStocks || []).join(', ')}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="impact-section">
                    <h5>📈 Market Sentiment</h5>
                    <div className="sentiment-analysis">
                      <div className="sentiment-item">
                        <strong>Current:</strong> <span className={getImpactColor(impactAnalysis.marketSentiment?.current)}>
                          {impactAnalysis.marketSentiment?.current || 'unknown'}
                        </span>
                      </div>
                      <div className="sentiment-item">
                        <strong>Predicted:</strong> {impactAnalysis.marketSentiment?.predicted || 'unknown'}
                      </div>
                      <div className="sentiment-item">
                        <strong>Confidence:</strong> {Math.round((impactAnalysis.marketSentiment?.confidence || 0) * 100)}%
                      </div>
                    </div>
                  </div>

                  <div className="impact-section">
                    <h5>⚠️ Risk Assessment</h5>
                    <div className="risk-details">
                      <div className="risk-level">
                        <strong>Risk Level:</strong> <span className={`risk-badge ${impactAnalysis.riskAssessment.level}`}>
                          {(impactAnalysis.riskAssessment.level || 'unknown').toUpperCase()}
                        </span>
                      </div>
                      <div className="risk-factors">
                        <strong>Key Factors:</strong>
                        <ul>
                          {(impactAnalysis.riskAssessment.factors || []).map((factor, index) => (
                            <li key={index}>{factor}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="risk-mitigation">
                        <strong>Mitigation Strategies:</strong>
                        <p>{impactAnalysis.riskAssessment?.mitigation || 'No specific mitigation strategies available.'}</p>
                      </div>
                    </div>
                  </div>

                  <div className="impact-section">
                    <h5>💡 Recommendations</h5>
                    <ul className="recommendations-list">
                      {(impactAnalysis.recommendations || []).map((rec, index) => (
                        <li key={index}>{rec}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsAnalysis;
